import { useTranslation } from "react-i18next";
import { StatusCode } from "api/protocols";
import classNames from "classnames";
import Checkbox from "components/form/checkbox/Checkbox";
import Sheet from "components/sheet/Sheet";
import { useAppDispatch } from "hooks/redux";
import React, { useEffect, useState } from "react";
// import { useTranslation } from "react-i18next";
import { dequeue, enqueue, showError } from "redux/actions/ui";
import { ICartLineItem } from "redux/reducers/models/cart";
import { ITournament } from "redux/reducers/models/tournament";
import {
  GetTournamentRegistrationFees,
  IRegistrationFee,
} from "api/rpc/2024-04/facilityAdmin/tournament/registration/registration";

interface ITournamentSelection {
  lineItem: ICartLineItem;
  tournaments: Array<IRegistrationFee>;
}

interface IProps {
  open: boolean;
  onCancel: () => void;
  onOk: (selectedTournaments: Array<string | number>) => void;
  tournamentFeeProducts: Array<ICartLineItem>;
}

interface IState {
  selectedTournaments: Map<number, ITournament>;
  tournaments: Array<ITournamentSelection>;
}

export default function TournamentSelectionModal(props: IProps) {
  const { t } = useTranslation();
  const { open, onCancel, onOk, tournamentFeeProducts } = props;
  const dispatch = useAppDispatch();

  const [state, setState] = useState<IState>({
    selectedTournaments: new Map(),
    tournaments: [],
  });

  useEffect(() => {
    if (tournamentFeeProducts && tournamentFeeProducts?.length > 0) {
      void loadTournaments();
    } else {
      setState(prevState => ({ ...prevState, selectedTournaments: new Map(), tournaments: [] }));
    }
  }, [tournamentFeeProducts]);

  async function loadTournaments() {
    const tournaments: Array<ITournamentSelection> = [];

    dispatch(enqueue());
    for (const lineItem of tournamentFeeProducts) {
      const tournamentRes = await GetTournamentRegistrationFees({ variant_id: lineItem?.variant_id }, false);
      if (tournamentRes.status !== StatusCode.OK) {
        dispatch(showError(t("Error getting tournaments")));
        dispatch(dequeue());
        return;
      }

      tournaments.push({ lineItem: lineItem, tournaments: tournamentRes.data });
    }

    dispatch(dequeue());

    setState(prevState => ({ ...prevState, tournaments }));
  }

  function handleCancel() {
    void onCancel();
    setState(prevState => ({ ...prevState, selectedTournaments: new Map(), tournaments: [] }));
  }

  function handleOk() {
    const selectedTournaments = Array.from(state.selectedTournaments, tournament => tournament[1]?.id);

    if (state?.selectedTournaments?.size === 0) {
      void onOk(null);
      setState(prevState => ({ ...prevState, selectedTournaments: new Map(), tournaments: [] }));
    } else {
      void onOk(selectedTournaments);
      setState(prevState => ({ ...prevState, selectedTournaments: new Map(), tournaments: [] }));
    }
  }

  function handleSelectTournament(tournament: ITournament, index: number) {
    const selectedTournaments = new Map(state.selectedTournaments);
    if (selectedTournaments?.get(index)?.id === tournament?.id) {
      selectedTournaments?.delete(index);
    } else {
      selectedTournaments.set(index, tournament);
    }
    setState(prevState => ({ ...prevState, selectedTournaments }));
  }

  return (
    <Sheet
      title={t("Select Tournaments")}
      open={open}
      size="small"
      closable
      stacked
      onCancel={handleCancel}
      onOk={handleOk}
      okText={state?.selectedTournaments?.size === 0 ? t("Skip") : t("Confirm Selection")}
    >
      <div className="ui-checkbox-group">
        {state?.tournaments?.map((tournament, tournamentIndex) => {
          return (
            <React.Fragment key={tournamentIndex}>
              <div className="mb-2 flex justify-between">
                <p>
                  {tournament?.lineItem?.product?.preferred_title
                    ? tournament?.lineItem?.product?.preferred_title
                    : tournament?.lineItem?.product?.title}
                </p>
                {tournament?.lineItem?.product_title !== tournament?.lineItem?.variant_title && (
                  <p className="text-xs">{tournament?.lineItem?.variant_title}</p>
                )}
              </div>
              {tournament?.tournaments?.map((tournamentFee, tournamentFeeIndex) => {
                return (
                  <div
                    key={tournamentFeeIndex}
                    onClick={() => handleSelectTournament(tournamentFee?.tournament, tournamentIndex)}
                    className={classNames("ui-checkbox-group-item items-center cursor-pointer", {
                      "ui-checkbox-group-item_selected":
                        state.selectedTournaments.get(tournamentIndex)?.id === tournamentFee?.tournament_id,
                    })}
                  >
                    <div className="ui-checkbox-group-item-content">
                      <div className="ui-checkbox-group-item-text">
                        <p className="text-sm text-medium text-gray-700">{tournamentFee?.tournament?.name}</p>
                      </div>
                    </div>

                    <Checkbox
                      size="medium"
                      value={tournamentFee.id}
                      checked={state?.selectedTournaments.get(tournamentIndex)?.id === tournamentFee?.tournament_id}
                    />
                  </div>
                );
              })}
              <br />
            </React.Fragment>
          );
        })}
      </div>
    </Sheet>
  );
}
